'use client';
import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { getActiveBatch } from '@/lib/api/batchfinder';
import { usePolling } from '@/hooks/usePolling';
import { BatchData, ActiveBatchResponse } from '../app/(protected)/batch/types';
import { useInstacartAuth } from './InstacartAuthContext';
interface ActiveBatchState {
  hasActiveBatch: boolean;
  batchData?: BatchData;
  lastChecked?: Date | null;
  isBotRunning: boolean;
}

interface ActiveBatchContextType extends ActiveBatchState {
  refetch: () => Promise<void>;
  setBotRunning: (running: boolean) => void;
}

const ActiveBatchContext = createContext<ActiveBatchContextType | undefined>(undefined);

export function ActiveBatchProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { sessionId } = useAuth();
  const { isInstacartLinked } = useInstacartAuth();
  const [isBotRunning, setIsBotRunning] = useState(false);


  const { data, isLoading, lastPollTime, refetch } = usePolling<ActiveBatchState>({
    key: 'active-batch',
    interval: 2 * 60 * 1000, // 2 minutes
    enabled: Boolean(sessionId) && isInstacartLinked, // Always run if sessionId exists
    fetchFn: async () => {
      if (!sessionId) {
        return { hasActiveBatch: false, isBotRunning };
      }

      const response = await getActiveBatch(sessionId);
      // console.log('[ActiveBatch] Response:', response);
      
      if (response.statusCode === 200 && response.body.batch) {
        // console.log('[ActiveBatch] Active batch found, redirecting to /batch');
        router.push('/batch');
        return {
          hasActiveBatch: true,
          batchData: response.body.batch,
          isBotRunning
        };
      }

      if (response.statusCode === 400 && response.body === 'Rate limited') {
        // console.log('[ActiveBatch] Rate limited, waiting 60 seconds...');
        await new Promise(resolve => setTimeout(resolve, 60 * 1000));
        // console.log('[ActiveBatch] Resuming after rate limit delay');
      }

      // console.log('[ActiveBatch] No active batch found');
      return {
        hasActiveBatch: false,
        batchData: undefined,
        isBotRunning
      };
    },
    onSuccess: () => {/* console.log('[ActiveBatch] Poll successful') */},
    onError: (error) => {/* console.log('[ActiveBatch] Poll error:', error) */}
  });

  const contextValue: ActiveBatchContextType = {
    hasActiveBatch: data?.hasActiveBatch ?? false,
    batchData: data?.batchData,
    lastChecked: lastPollTime,
    isBotRunning,
    refetch,
    setBotRunning: setIsBotRunning
  };

  return (
    <ActiveBatchContext.Provider value={contextValue}>
      {children}
    </ActiveBatchContext.Provider>
  );
}

export function useActiveBatch() {
  const context = useContext(ActiveBatchContext);
  if (context === undefined) {
    throw new Error('useActiveBatch must be used within an ActiveBatchProvider');
  }
  return context;
}
